import { Tag } from '@phosphor-icons/react';
import { RowActions } from '../common/RowActions';
import { SortableList } from '../common/SortableList';
import { cn } from '../../utils/cn';
import pt from '../../locales/pt';
import {
  allocationBadgeClasses,
  allocationBadgeLabel,
  resolveCategoryAllocation,
} from '../../utils/categoryAllocation';

function CategoryColorDot({ category }) {
  const color = category.color || '#7C9E6F';
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
      style={{ backgroundColor: `${color}33` }}
    >
      <Tag className="h-3.5 w-3.5" style={{ color }} aria-hidden />
    </span>
  );
}

function CategoryBadges({ category }) {
  const allocation = resolveCategoryAllocation(category);
  return (
    <div className="flex flex-wrap gap-1">
      <span
        className={cn(
          'rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide',
          allocationBadgeClasses(allocation),
        )}
      >
        {allocationBadgeLabel(allocation, pt)}
      </span>
      <span className="rounded-full bg-muted px-1.5 py-px text-[9px] font-medium text-muted-foreground">
        {category.frequency === 'regular'
          ? pt.categories.form.frequencyRegular
          : pt.categories.form.frequencyOccasional}
      </span>
    </div>
  );
}

export function CategoryRow({
  category,
  isOrphan = false,
  dragHandle = null,
  actions,
  className,
}) {
  return (
    <div
      className={cn(
        'box-border flex min-w-0 items-center gap-2 rounded-xl border border-border/90 bg-card px-2.5 py-2 shadow-sm md:gap-2.5 md:px-3 md:py-2.5',
        isOrphan && 'bg-warning-50/40',
        className,
      )}
    >
      {dragHandle}
      <CategoryColorDot category={category} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{category.name}</p>
        <p className="truncate text-[11px] text-muted-foreground md:text-xs">
          {category.group_name || pt.categories.form.noGroup}
        </p>
        <div className="mt-1">
          <CategoryBadges category={category} />
        </div>
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-0.5">{actions}</div>
      ) : null}
    </div>
  );
}

export function SortableCategoryList({
  categories,
  orphans,
  onReorder,
  onEdit,
  onArchive,
}) {
  return (
    <SortableList
      items={categories}
      onReorder={onReorder}
      renderItem={({ item: category, dragHandle }) => (
        <CategoryRow
          category={category}
          isOrphan={orphans.some((o) => o.id === category.id)}
          dragHandle={dragHandle}
          actions={
            <RowActions
              item={category}
              onEdit={onEdit}
              onArchive={onArchive}
              editLabel={pt.categories.actions.edit}
              archiveLabel={pt.categories.actions.archive}
            />
          }
        />
      )}
    />
  );
}
